import React from "react";

const MainArea = ({
  section,
  resumeData,
  handleChange,
  addSection,
  removeSection,
}) => {
  return (
    <div className="flex-1 bg-gray-100 p-10">
      <h2 className="text-2xl font-bold mb-5">Fill Out Your Resume</h2>

      {/* Personal Info */}
      {section === "personal" && (
        <div className="p-4 border rounded-lg bg-white shadow">
          {["fullName", "email", "phone", "location"].map((field) => (
            <div key={field}>
              <label className="block mb-2 capitalize">
                {field.replace(/([A-Z])/g, " $1")}
              </label>
              <input
                type={field === "email" ? "email" : "text"}
                value={resumeData.personal[field]}
                onChange={(e) =>
                  handleChange("personal", 0, field, e.target.value)
                }
                className="w-full p-2 border rounded-lg mb-4"
              />
            </div>
          ))}
        </div>
      )}

      {/* Work Experience / Education */}
      {["experience", "education"].includes(section) && (
        <>
          <button
            onClick={() =>
              addSection(
                section,
                section === "experience"
                  ? {
                      company: "",
                      role: "",
                      startDate: "",
                      endDate: "",
                      stillWorking: false,
                      location: "",
                      details: [""],
                    }
                  : {
                      college: "",
                      passingYear: "",
                      course: "",
                      location: "",
                      details: [""],
                    }
              )
            }
            className="bg-blue-500 text-white px-3 py-1 mb-4"
          >
            + Add {section.charAt(0).toUpperCase() + section.slice(1)}
          </button>

          {resumeData[section].map((item, index) => (
            <div
              key={index}
              className="mb-2 p-2 border rounded-lg bg-white shadow"
            >
              {section === "experience" ? (
                <>
                  {["company", "role", "location"].map((field) => (
                    <input
                      key={field}
                      type="text"
                      placeholder={field.replace(/([A-Z])/g, " $1")}
                      value={item[field]}
                      onChange={(e) =>
                        handleChange(section, index, field, e.target.value)
                      }
                      className="w-full p-2 border rounded-lg mb-2"
                    />
                  ))}
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={item.startDate}
                      onChange={(e) =>
                        handleChange(
                          section,
                          index,
                          "startDate",
                          e.target.value
                        )
                      }
                      className="p-2 border rounded-lg w-1/2"
                    />
                    <input
                      type="date"
                      value={item.endDate}
                      onChange={(e) =>
                        handleChange(section, index, "endDate", e.target.value)
                      }
                      disabled={item.stillWorking}
                      className="p-2 border rounded-lg w-1/2"
                    />
                  </div>
                  <label className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      checked={item.stillWorking}
                      onChange={(e) =>
                        handleChange(
                          section,
                          index,
                          "stillWorking",
                          e.target.checked
                        )
                      }
                      className="mr-2"
                    />
                    Still Working
                  </label>
                </>
              ) : (
                <>
                  {["college", "course", "location", "passingYear"].map(
                    (field) => (
                      <input
                        key={field}
                        type="text"
                        placeholder={field.replace(/([A-Z])/g, " $1")}
                        value={item[field]}
                        onChange={(e) =>
                          handleChange(section, index, field, e.target.value)
                        }
                        className="w-full p-2 border rounded-lg mb-2"
                      />
                    )
                  )}
                </>
              )}

              {/* Details */}
              <h4 className="font-bold mt-2">Details:</h4>
              {item.details.map((detail, detailIndex) => (
                <div key={detailIndex} className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Detail"
                    value={detail}
                    onChange={(e) => {
                      const updatedDetails = [...item.details];
                      updatedDetails[detailIndex] = e.target.value;
                      handleChange(section, index, "details", updatedDetails);
                    }}
                    className="w-full p-2 border rounded-lg mb-2"
                  />
                  <button
                    onClick={() => {
                      const updatedDetails = item.details.filter(
                        (_, i) => i !== detailIndex
                      );
                      handleChange(section, index, "details", updatedDetails);
                    }}
                    className="bg-red-500 text-white px-3 py-1"
                  >
                    -
                  </button>
                </div>
              ))}
              <button
                onClick={() =>
                  handleChange(section, index, "details", [...item.details, ""])
                }
                className="bg-green-500 text-white px-3 py-1"
              >
                + Add Detail
              </button>

              <button
                onClick={() => removeSection(section, index)}
                className="bg-red-500 text-white px-3 py-1 mt-2"
              >
                Remove
              </button>
            </div>
          ))}
        </>
      )}

      {/* Skills Section */}
      {section === "skills" && (
        <>
          <button
            onClick={() => addSection("skills", "")}
            className="bg-blue-500 text-white px-3 py-1 mb-4"
          >
            + Add Skill
          </button>

          {resumeData.skills.map((skill, index) => (
            <div key={index} className="mb-2 flex">
              <input
                type="text"
                value={skill}
                onChange={(e) =>
                  handleChange("skills", index, null, e.target.value)
                }
                className="w-full p-2 border rounded-lg"
              />
              <button
                onClick={() => removeSection("skills", index)}
                className="ml-2 bg-red-500 text-white px-3"
              >
                X
              </button>
            </div>
          ))}
        </>
      )}

      {/* Certificates Section */}
      {section === "certificates" && (
        <>
          <button
            onClick={() =>
              addSection("certificates", {
                title: "",
                organization: "",
                year: "",
              })
            }
            className="bg-blue-500 text-white px-3 py-1 mb-4"
          >
            + Add Certificate
          </button>

          {resumeData.certificates.map((cert, index) => (
            <div
              key={index}
              className="mb-2 p-2 border rounded-lg bg-white shadow"
            >
              <input
                type="text"
                placeholder="Certificate Name"
                value={cert.title}
                onChange={(e) =>
                  handleChange("certificates", index, "title", e.target.value)
                }
                className="w-full p-2 border rounded-lg mb-2"
              />
              <button
                onClick={() => removeSection("certificates", index)}
                className="bg-red-500 text-white px-3 py-1"
              >
                Remove
              </button>
            </div>
          ))}
        </>
      )}

      {/* Languages Section */}
      {section === "languages" && (
        <>
          <button
            onClick={() =>
              addSection("languages", { name: "", proficiency: "Beginner" })
            }
            className="bg-blue-500 text-white px-3 py-1 mb-4"
          >
            + Add Language
          </button>

          {resumeData.languages.map((lang, index) => (
            <div
              key={index}
              className="mb-2 p-2 border rounded-lg bg-white shadow"
            >
              <input
                type="text"
                placeholder="Language Name"
                value={lang.name}
                onChange={(e) =>
                  handleChange("languages", index, "name", e.target.value)
                }
                className="w-full p-2 border rounded-lg mb-2"
              />
              <select
                value={lang.proficiency}
                onChange={(e) =>
                  handleChange(
                    "languages",
                    index,
                    "proficiency",
                    e.target.value
                  )
                }
                className="w-full p-2 border rounded-lg mb-2"
              >
                {[
                  "Beginner",
                  "Intermediate",
                  "Advanced",
                  "Fluent",
                  "Native",
                ].map((level) => (
                  <option key={level}>{level}</option>
                ))}
              </select>

              <button
                onClick={() => removeSection("languages", index)}
                className="bg-red-500 text-white px-3 py-1"
              >
                Remove
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default MainArea;
